// src/features/landing/CreatePageDialog.tsx
// Start a new landing page from scratch. CreateLandingPage.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { landingApi } from '@core/api';
import type { LandingPage } from '@core/types/landing';
import { LandingModal } from './Modal';
import { errMessage, makeEntity, type ScopeIds } from './helpers';

interface CreatePageDialogProps {
  open: boolean;
  onClose: () => void;
  scope: ScopeIds;
  onCreated: (page: LandingPage) => void;
}

export function CreatePageDialog({ open, onClose, scope, onCreated }: CreatePageDialogProps) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slugify = (v: string) =>
    v
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const save = async () => {
    if (!title.trim()) {
      setError(t('landing.pages.titleRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await landingApi.CreateLandingPage({
        entity: makeEntity('', 'landing_page', scope),
        title: title.trim(),
        description,
        slug: slug.trim() || slugify(title),
      });
      if (res.page) onCreated(res.page);
      setTitle('');
      setSlug('');
      setDescription('');
      onClose();
    } catch (e) {
      setError(errMessage(e, t('landing.pages.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <LandingModal
      open={open}
      onClose={onClose}
      title={t('landing.pages.create')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('common.create')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="space-y-3">
        <Input label={t('landing.pages.pageTitle')} value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Input label={t('landing.pages.slug')} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(title) || 'summer-sale-2026'} helperText="/l/your-slug" />
        <Textarea label={t('landing.pages.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </div>
    </LandingModal>
  );
}
